import React from 'react'
import { NavLink, type NavLinkProps } from 'react-router-dom'

import Spinner from '../spinner'

import './styles.scss'

export type ButtonAs = 'button' | 'link' | 'dropdown'

export enum ButtonVariant {
  PRIMARY = 'btn-primary',
  SECONDARY = 'btn-light',
  LIGHT = 'btn-outline-primary',
  PLAIN = 'btn-plain',
}

export enum ButtonSize {
  DEFAULT = '',
  SMALL = 'btn-sm',
  LARGE = 'btn-lg',
  AUTO = 'auto',
}

type CommonProps = {
  children: React.ReactNode
  variant?: ButtonVariant
  /** Omit for fixed-width behaviour; pass `AUTO` for content width. */
  size?: ButtonSize
  /** On mobile screens, force width to 100% when set to 'full'. */
  mobileWidth?: 'full'
  disabled?: boolean
  loading?: boolean
  loadingText?: string
  /** When true with loading, render loadingText visibly next to the spinner. */
  visibleLoadingText?: boolean
  icon?: React.ReactElement
  iconPosition?: 'start' | 'end'
  className?: string
}

type ButtonNativeProps = CommonProps & {
  as?: 'button' | 'dropdown'
  type?: 'button' | 'submit' | 'reset'
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children' | 'className' | 'size'>

type ButtonLinkProps = CommonProps & {
  as: 'link'
  to: NavLinkProps['to']
} & Omit<NavLinkProps, 'to' | 'children' | 'className' | 'size'>

export type ButtonProps = ButtonNativeProps | ButtonLinkProps

function buildButtonClasses(
  variant: ButtonVariant,
  size: ButtonSize | undefined,
  mobileWidth: 'full' | undefined,
  hasIcon: boolean,
  as: ButtonAs
): string {
  const widthMod = size === ButtonSize.AUTO ? 'button--width-auto' : 'button--width-fixed'
  const sizeClass = size === undefined || size === ButtonSize.DEFAULT || size === ButtonSize.AUTO ? '' : size
  const mobileWidthMod = mobileWidth === 'full' ? 'button--mobile-full' : ''
  const iconMod = hasIcon ? 'button--with-icon' : ''
  const asMod = as === 'link' ? 'button--as-link' : as === 'dropdown' ? 'button--as-dropdown' : ''

  return ['button', 'btn', variant, sizeClass, widthMod, mobileWidthMod, iconMod, asMod].filter(Boolean).join(' ')
}

const Button: React.FC<ButtonProps> = (props) => {
  const {
    children,
    variant = ButtonVariant.PRIMARY,
    size,
    mobileWidth,
    icon,
    iconPosition = 'end',
    loading,
    loadingText = 'Loading',
    visibleLoadingText = false,
    disabled,
    className = '',
  } = props

  const resolvedAs: ButtonAs =
    props.as === 'link' ? 'link' : props.as === 'dropdown' ? 'dropdown' : 'button'

  const iconEl = icon ? (
    <span className={`button-icon ${children ? iconPosition === 'start' ? 'me-2' : 'ms-2' : ''}`}>{icon}</span>
  ) : null

  const mergedClassName = [
    buildButtonClasses(variant, size, mobileWidth, Boolean(icon) || Boolean(loading), resolvedAs),
    loading ? 'button--with-icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')
    .trim()

  const loadingInner = visibleLoadingText ? (
    <>
      <Spinner /> <span className='ms-2'>{loadingText}</span>
    </>
  ) : (
    <>
      <Spinner /><span className='ms-2 sr-only'>{loadingText}</span>
    </>
  )

  const inner = (
    <>
      {!loading && iconPosition === 'start' ? iconEl : null}
      {loading ? loadingInner : children}
      {!loading && iconPosition === 'end' ? iconEl : null}
    </>
  )

  if (resolvedAs === 'link') {
    const { as: _a, to, children: _c, variant: _v, size: _s, mobileWidth: _mw, icon: _i, iconPosition: _ip, loading: _l, loadingText: _lt, visibleLoadingText: _vl, disabled: _d, className: _cn, ...navProps } =
      props as ButtonLinkProps
    return (
      <NavLink to={to} className={mergedClassName} {...navProps}>
        {inner}
      </NavLink>
    )
  }

  const {
    as: _as,
    to: _to,
    children: _ch,
    variant: _va,
    size: _si,
    mobileWidth: _mw2,
    icon: _ic,
    iconPosition: _ip2,
    loading: _lo,
    loadingText: _ltx,
    visibleLoadingText: _vl2,
    className: _cl,
    type = 'button',
    ...buttonProps
  } = props as ButtonNativeProps & { to?: unknown }

  return (
    <button type={type} className={mergedClassName} disabled={loading || disabled} {...buttonProps}>
      {inner}
    </button>
  )
}

export default Button
